import React from 'react';
import { useTranslation } from 'react-i18next';

export interface TagProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'> {
  /** Tag label text */
  label: string;
  /** Tag variant/color scheme */
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'gray';
  /** Size of the tag */
  size?: 'sm' | 'md' | 'lg';
  /** Show remove/close button */
  removable?: boolean;
  /** Handler for remove button click */
  onRemove?: () => void;
  /** Icon to display at the start of the tag */
  icon?: React.ReactNode;
  /** Make tag a pill shape (fully rounded) */
  pill?: boolean;
  /** Apply outlined style instead of filled */
  outlined?: boolean;
  /** Whether the tag is disabled */
  disabled?: boolean;
  /** Extra class names */
  className?: string;
}

export const Tag = React.forwardRef<HTMLDivElement, TagProps>(
  (
    {
      label,
      variant = 'default',
      size = 'md',
      removable = false,
      onRemove,
      icon,
      pill = false,
      outlined = false,
      disabled = false,
      className = '',
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    
    // Size classes
    const sizeClasses = {
      sm: 'text-xs px-2 py-0.5',
      md: 'text-sm px-2.5 py-1',
      lg: 'text-base px-3 py-1.5',
    };
    
    // Border radius based on pill prop
    const radiusClasses = pill ? 'rounded-full' : 'rounded-md';
    
    // Base variant colors for filled tags
    const filledVariantClasses = {
      default: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
      primary: 'bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-200',
      success: 'bg-green-100 text-green-800 dark:bg-green-900/60 dark:text-green-200',
      warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/60 dark:text-yellow-200',
      danger: 'bg-red-100 text-red-800 dark:bg-red-900/60 dark:text-red-200',
      info: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/60 dark:text-indigo-200',
      gray: 'bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-200',
    };
    
    // Base variant colors for outlined tags
    const outlinedVariantClasses = {
      default: 'border border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300',
      primary: 'border border-blue-500 text-blue-700 dark:border-blue-700 dark:text-blue-400',
      success: 'border border-green-500 text-green-700 dark:border-green-700 dark:text-green-400',
      warning: 'border border-yellow-500 text-yellow-700 dark:border-yellow-700 dark:text-yellow-400',
      danger: 'border border-red-500 text-red-700 dark:border-red-700 dark:text-red-400',
      info: 'border border-indigo-500 text-indigo-700 dark:border-indigo-700 dark:text-indigo-400',
      gray: 'border border-gray-400 text-gray-700 dark:border-gray-500 dark:text-gray-300',
    };
    
    // Close button size classes
    const closeButtonSizeClasses = {
      sm: 'w-3.5 h-3.5 ml-1',
      md: 'w-4 h-4 ml-1.5',
      lg: 'w-5 h-5 ml-2',
    };
    
    const handleRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (!disabled && onRemove) {
        onRemove();
      }
    };
    
    return (
      <div
        ref={ref}
        className={`
          inline-flex items-center
          ${sizeClasses[size]}
          ${radiusClasses}
          ${outlined ? outlinedVariantClasses[variant] : filledVariantClasses[variant]}
          ${disabled ? 'opacity-60 cursor-not-allowed' : ''}
          font-medium
          ${className}
        `}
        {...props}
      >
        {icon && (
          <span className="mr-1.5 flex-shrink-0">
            {icon}
          </span>
        )}
        
        <span>{label}</span>
        
        {removable && (
          <button
            type="button"
            onClick={handleRemove}
            disabled={disabled}
            className={`
              ${closeButtonSizeClasses[size]}
              rounded-full
              focus:outline-none
              ${disabled ? 'cursor-not-allowed' : 'hover:bg-black/10 dark:hover:bg-white/10'}
              inline-flex items-center justify-center
            `}
            aria-label={t('glossary.remove') as string}
          >
            <span className="sr-only">{t('glossary.remove') as string}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="w-full h-full"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )}
      </div>
    );
  }
);

Tag.displayName = 'Tag';

export default Tag;